import { View, Text } from "@react-pdf/renderer";
import { abnNoticeConstants } from "constants/abnNotice";
import { useMemo } from "react";
import { IAbnNotice, IOptionDto } from "types/AbnNotice/AbnNotice";

import { styles } from "../styles";

const bulletStyle = {
  ...styles.bodyText,
  fontSize: 11,
  marginBottom: 2,
};

/**
 * Splits an option's text into the portion CMS-R-131 prints in bold and the remainder.
 *
 * `boldPrefix` is presentational metadata (CLE-2659) and is absent from ABNs signed before it was
 * introduced, and defensively ignored if it ever stops being a literal prefix of the label — in
 * either case the whole label renders unbolded rather than being silently truncated.
 */
export function splitOptionText(option: IOptionDto): {
  bold: string;
  rest: string;
} {
  const { boldPrefix, label } = option;
  if (!boldPrefix || !label?.startsWith(boldPrefix)) {
    return { bold: "", rest: label ?? "" };
  }
  return { bold: boldPrefix, rest: label.slice(boldPrefix.length) };
}

/**
 * CLE-2659: "What to do now" and the boxed option list of CMS-R-131 (Exp. 03/31/2029).
 */
export function Options({ activeAbnNotice }: { activeAbnNotice: IAbnNotice }) {
  const options = useMemo(() => {
    const allOptions =
      activeAbnNotice?.documentSnapshot?.availableOptions || [];
    // CMS requires the decline option to be Option 3. Match on value rather than on the
    // label text, which changes whenever CMS revises the form.
    const declineOption = allOptions.find(
      (option: any) =>
        option.value === abnNoticeConstants.abnOption.declineService
    );
    if (!declineOption) return allOptions;

    return [
      ...allOptions.filter((option: any) => option !== declineOption),
      declineOption,
    ];
  }, [activeAbnNotice]);

  const selectedOptionValue = useMemo(() => {
    return activeAbnNotice?.documentSnapshot?.selectedOption?.value;
  }, [activeAbnNotice]);

  return (
    <View style={{ display: "flex", flexDirection: "column" }}>
      <Text
        style={{
          ...styles.bodyText,
          fontFamily: "Helvetica-Bold",
          fontSize: 12,
          marginBottom: 3,
        }}
      >
        What to do now
      </Text>
      <Text style={bulletStyle}>
        • Read this notice to make an informed decision about your care.
      </Text>
      <Text style={bulletStyle}>• Ask any questions you have.</Text>
      <Text style={{ ...bulletStyle, marginBottom: 6 }}>
        • Choose one option below to let us know if you still want to get the
        item, test, service or care.
      </Text>

      <View
        style={{
          borderWidth: 1,
          borderColor: "#000",
          padding: 6,
        }}
      >
        <Text
          style={{
            ...styles.bodyText,
            fontFamily: "Helvetica-Bold",
            fontSize: 11,
            marginBottom: 3,
          }}
        >
          Choose ONE option below. We can&apos;t choose for you.
        </Text>
        <Text style={{ ...styles.bodyText, fontSize: 11, marginBottom: 6 }}>
          {activeAbnNotice?.documentSnapshot?.note ||
            `If you choose Option 1 or 2, we may help you use any other insurance you might have, but Medicare can't require us to do this.`}
        </Text>

        {options.map((option: IOptionDto, index: number) => {
          const { bold, rest } = splitOptionText(option);
          return (
            <View
              key={option.value}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                marginBottom: index === options.length - 1 ? 0 : 6,
              }}
            >
              {/* Radio circle — filled for the option the patient chose. */}
              <View
                style={{
                  width: 10,
                  height: 10,
                  borderWidth: 1,
                  borderColor: "#000",
                  borderRadius: 5,
                  marginRight: 5,
                  marginTop: 2,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {option.value === selectedOptionValue && (
                  <View
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "#000",
                    }}
                  />
                )}
              </View>

              <Text
                style={{
                  ...styles.bodyText,
                  fontSize: 11,
                  marginBottom: 0,
                  flex: 1,
                }}
              >
                <Text style={{ fontFamily: "Helvetica-Bold" }}>
                  {`Option ${index + 1}: `}
                  {bold}
                </Text>
                {rest}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}
